/**
 * The no-flash header logo cache (XC-ERP1-U11 §2.2).
 *
 * The last processed logo is kept as a data URL in localStorage, keyed by
 * tenant + version, so a reload paints it before the branding call returns.
 * The answer itself (entitled or not, logo or Wingo) is cached too, so a
 * non-entitled tenant paints the Wingo logo at once instead of a placeholder.
 *
 * Two keys per tenant: an index naming the version and mode, and the version's
 * data URL. Every storage access is wrapped: private mode, a full quota or
 * blocked storage only cost the cache, never the header.
 *
 * A logo is not personal data, and the cache is keyed by tenant (not user), so
 * it outlives a sign-out the way the logo would on the next sign-in anyway.
 */

import type { BrandingDisplayMode, BrandingKind } from '@/services/brandingService';

const PREFIX = 'askwingo-branding';

/** Processed logos are ~60 KB; refuse anything far past that rather than fill the quota. */
export const MAX_CACHED_DATA_URL_CHARS = 200_000;

const DATA_URL_RE = /^data:image\/(png|webp|jpeg);base64,[A-Za-z0-9+/=]+$/;

export interface BrandingCacheIndex {
  entitled: boolean;
  kind: BrandingKind;
  version: number | null;
  display_mode: BrandingDisplayMode;
}

export interface BrandingCacheEntry extends BrandingCacheIndex {
  /** The version's logo as a data URL; null when none is cached (or none is live). */
  data_url: string | null;
}

export const brandingIndexKey = (tenantId: string) => `${PREFIX}:${tenantId}`;
export const brandingLogoKey = (tenantId: string, version: number) =>
  `${PREFIX}:${tenantId}:v${version}`;

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeGet(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): boolean {
  try {
    const s = storage();
    if (!s) return false;
    s.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function safeRemove(key: string): void {
  try {
    storage()?.removeItem(key);
  } catch {
    /* storage unavailable: nothing to remove */
  }
}

function parseIndex(raw: string | null): BrandingCacheIndex | null {
  if (raw === null) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof v !== 'object' || v === null) return null;
  const o = v as Record<string, unknown>;
  if (typeof o.entitled !== 'boolean') return null;
  if (o.kind !== 'logo' && o.kind !== 'wingo_default') return null;
  if (o.version !== null && !(typeof o.version === 'number' && Number.isInteger(o.version))) {
    return null;
  }
  if (o.display_mode !== 'plain' && o.display_mode !== 'light_badge') return null;
  return {
    entitled: o.entitled,
    kind: o.kind,
    version: o.version as number | null,
    display_mode: o.display_mode,
  };
}

const showsLogo = (i: BrandingCacheIndex) =>
  i.entitled && i.kind === 'logo' && i.version !== null;

/** The cached answer for this tenant, or null when there is none (or storage fails). */
export function readBrandingCache(tenantId: string): BrandingCacheEntry | null {
  if (!tenantId) return null;
  const index = parseIndex(safeGet(brandingIndexKey(tenantId)));
  if (!index) return null;
  let dataUrl: string | null = null;
  if (showsLogo(index)) {
    const raw = safeGet(brandingLogoKey(tenantId, index.version as number));
    dataUrl = raw !== null && DATA_URL_RE.test(raw) ? raw : null;
  }
  return { ...index, data_url: dataUrl };
}

/**
 * Record the tenant's current answer. A new version (or a revert) drops the
 * previous version's data URL; ``dataUrl`` is stored only for a live logo.
 */
export function writeBrandingCache(
  tenantId: string,
  index: BrandingCacheIndex,
  dataUrl: string | null,
): void {
  if (!tenantId) return;
  const previous = parseIndex(safeGet(brandingIndexKey(tenantId)));
  if (previous?.version != null && previous.version !== index.version) {
    safeRemove(brandingLogoKey(tenantId, previous.version));
  }
  const clean: BrandingCacheIndex = {
    entitled: index.entitled,
    kind: index.kind,
    version: index.version,
    display_mode: index.display_mode,
  };
  if (!safeSet(brandingIndexKey(tenantId), JSON.stringify(clean))) return;
  if (!showsLogo(clean)) {
    if (clean.version !== null) safeRemove(brandingLogoKey(tenantId, clean.version));
    return;
  }
  const logoKey = brandingLogoKey(tenantId, clean.version as number);
  if (dataUrl && dataUrl.length <= MAX_CACHED_DATA_URL_CHARS && DATA_URL_RE.test(dataUrl)) {
    safeSet(logoKey, dataUrl);
  } else if (dataUrl !== null) {
    safeRemove(logoKey);
  }
}
